import { createReducer } from 'typesafe-actions';
import {
  addTokensBalanceToTotalBalance,
  getAccountDetailsAsync,
  resetAccountDetails,
  updateActiveAccountHashes,
} from './actions';
import {
  AccountInfoResult,
  DeployContractPackageResult,
  FTBalanceTokenResult,
} from '@block-explorer/entities';
import Big from 'big.js';

export enum ValidatorType {
  ACTIVE_VALIDATOR = 'active_validator',
  PAST_VALIDATOR = 'past_validator',
  PENDING_VALIDATOR = 'pending_validator',
  ACTIVE_VALIDATOR_BID = 'active_bidder',
  INACTIVE_VALIDATOR_BID = 'inactive_bidder',
}

type AccountBalances = {
  ftTokensTotalBalance: string;
  totalBalanceMotes: string;
  genesisBalanceMotes: string;
  accountBalanceMotes: string;
  validatorStakedBalanceMotes: string;
  delegatorStakedBalanceMotes: string;
  undelegatingBalanceMotes: string;
  validatorsUnstakingBalanceMotes: string;
};

export type State = {
  accountHash: string | null;
  publicKey: string | undefined;
  accountInfo: AccountInfoResult | null;
  balance: AccountBalances;
  bidders: any;
  unstacking: any;
  validatorTotalRewards: any;
  validatorType: string | undefined;
  ftTokens: FTBalanceTokenResult[] | null;
  contractPackages: DeployContractPackageResult[] | null;
  loading: boolean;
  error?: string | null;
};

const initialState: State = {
  accountHash: null,
  publicKey: undefined,
  accountInfo: null,
  bidders: null,
  unstacking: null,
  validatorTotalRewards: null,
  validatorType: undefined,
  ftTokens: null,
  contractPackages: null,
  balance: {
    ftTokensTotalBalance: '0',
    totalBalanceMotes: '0',
    genesisBalanceMotes: '0',
    accountBalanceMotes: '0',
    validatorStakedBalanceMotes: '0',
    delegatorStakedBalanceMotes: '0',
    undelegatingBalanceMotes: '0',
    validatorsUnstakingBalanceMotes: '0',
  },
  loading: false,
};

const calculateTotalBalanceMotes = (accountInfo, unstacking, bidders) => {
  const undelegatingBalance = accountInfo?.data?.undelegating_balance || '0';
  const accountBalance = accountInfo?.data?.balance || '0';
  const delegatorStakedBalance = accountInfo?.data?.delegated_balance || '0';
  const genesisBalance = accountInfo?.data?.genesis_balance || '0';
  const validatorsUnstakingBalance = unstacking?.data || '0';
  const validatorStakedBalance = bidders?.data?.bidAmount || '0';

  const totalBalance = Big(accountBalance)
    .add(delegatorStakedBalance)
    .add(undelegatingBalance)
    .add(validatorStakedBalance)
    .add(validatorsUnstakingBalance)
    .toString();

  return {
    totalBalanceMotes: totalBalance,
    genesisBalanceMotes: genesisBalance,
    accountBalanceMotes: accountBalance,
    validatorStakedBalanceMotes: validatorStakedBalance,
    delegatorStakedBalanceMotes: delegatorStakedBalance,
    undelegatingBalanceMotes: undelegatingBalance,
    validatorsUnstakingBalanceMotes: validatorsUnstakingBalance,
  };
};

export default createReducer(initialState)
  .handleAction(getAccountDetailsAsync.request, (state, action): State => {
    return {
      ...state,
      loading: true,
      error: null,
    };
  })
  .handleAction(getAccountDetailsAsync.success, (state, action): State => {
    const [
      unstacking,
      bidders,
      account,
      validatorTotalRewards,
      ftTokens,
      contractPackages,
    ] = action.payload;

    const auctionStatus = account?.data?.auction_status;

    const validatorType =
      !auctionStatus && validatorTotalRewards.data
        ? ValidatorType.PAST_VALIDATOR
        : auctionStatus;

    return {
      ...state,
      accountInfo: account.data,
      publicKey: account.data?.public_key || state.publicKey,
      bidders: bidders.data,
      unstacking: unstacking.data,
      validatorTotalRewards: validatorTotalRewards.data,
      validatorType: validatorType,
      ftTokens: ftTokens.data,
      contractPackages: contractPackages.data,
      balance: {
        ...state.balance,
        ...calculateTotalBalanceMotes(account, unstacking, bidders),
      },
      loading: false,
      error: null,
    };
  })
  .handleAction(getAccountDetailsAsync.failure, (state, action): State => {
    return {
      ...state,
      loading: false,
      error: action.payload.error?.message || null,
    };
  })
  .handleAction(addTokensBalanceToTotalBalance, (state, action): State => {
    return {
      ...state,
      balance: {
        ...state.balance,
        ftTokensTotalBalance: action.payload.totalTokensBalanceInCurrency,
      },
    };
  })
  .handleAction(updateActiveAccountHashes, (state, action): State => {
    return {
      ...state,
      accountHash: action.payload.accountHash,
      publicKey: action.payload.publicKey,
    };
  })
  .handleAction(
    resetAccountDetails,
    (state): State => ({
      ...state,
      ...initialState,
    })
  );
